/**
 * System-wide toast via antd notification (top-right).
 *
 * - success / info: auto-dismiss
 * - error with sticky:true: stays until manual close; optional entity link
 */

import React, { createContext, useCallback, useContext, useMemo } from "react";
import { notification } from "antd";
import { Link } from "react-router-dom";

const ToastContext = createContext(null);

const DEFAULT_SUCCESS_MS = 4000;
const DEFAULT_INFO_MS = 4000;

function buildDescription({ description, href, linkLabel }) {
  if (!href && !description) return undefined;
  return (
    <>
      {description ? <span>{description}</span> : null}
      {href ? (
        <>
          {description ? <br /> : null}
          <Link to={href}>{linkLabel || "Open"}</Link>
        </>
      ) : null}
    </>
  );
}

export function ToastProvider({ children }) {
  const [api, contextHolder] = notification.useNotification({
    placement: "topRight",
    maxCount: 5,
  });

  const success = useCallback(
    (message, options = {}) => {
      const { duration = DEFAULT_SUCCESS_MS / 1000, description, key } = options;
      api.success({
        message,
        description,
        duration,
        key,
        placement: "topRight",
      });
    },
    [api]
  );

  const info = useCallback(
    (message, options = {}) => {
      const { duration = DEFAULT_INFO_MS / 1000, description, key } = options;
      api.info({
        message,
        description,
        duration,
        key,
        placement: "topRight",
      });
    },
    [api]
  );

  const error = useCallback(
    (message, options = {}) => {
      const {
        sticky = true,
        duration,
        description,
        href,
        linkLabel,
        key,
      } = options;
      const resolvedDuration =
        sticky || duration === 0 ? 0 : duration ?? DEFAULT_SUCCESS_MS / 1000;
      api.error({
        message,
        description: buildDescription({ description, href, linkLabel }),
        duration: resolvedDuration,
        key,
        placement: "topRight",
      });
    },
    [api]
  );

  const close = useCallback(
    (key) => {
      if (key) api.destroy(key);
      else api.destroy();
    },
    [api]
  );

  const value = useMemo(
    () => ({ success, error, info, close }),
    [success, error, info, close]
  );

  return (
    <ToastContext.Provider value={value}>
      {contextHolder}
      {children}
    </ToastContext.Provider>
  );
}

/**
 * @returns {{
 *   success: (message: string, options?: object) => void,
 *   error: (message: string, options?: object) => void,
 *   info: (message: string, options?: object) => void,
 *   close: (key?: string) => void,
 * }}
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}

export default ToastProvider;
